import { FormattedMessage, useIntl } from 'react-intl'
import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import Caption from 'LuxKit/Typography/Caption'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import type { CruiseOfferPriceVariation } from 'lib/cruises/cruiseUtils'
import CruisePromotionPriceUnit from 'components/Cruises/Common/CruisePromotionPriceUnit'
import useCruiseCabinTypeLabels from 'hooks/Cruise/useCruiseCabinTypeLabels'

export interface PriceRoomTypeProps {
  offer?: App.CruiseOffer
  priceFrom: App.CruisePriceDetails
  price: CruiseOfferPriceVariation
  memberPrice?: CruiseOfferPriceVariation
  memberPriceType?: App.Cruises.CruiseMemberPriceType
  header?: boolean
  hasValidDepartures?: boolean
}

function CruisePriceRoomTypeInfo({ priceFrom, price, memberPrice, memberPriceType, hasValidDepartures, header = false }: PriceRoomTypeProps) {
  const intl = useIntl()
  const cabinTypeLabels = useCruiseCabinTypeLabels()
  const promotionalPrice = price.promotionalPrice

  const fromCaption = intl.formatMessage({
    id: 'lZ-S_ZGKEPbpKqabJg9G',
    defaultMessage: 'From',
    description: 'Price caption indicating starting price for cruise offer',
  })

  return (
    <>
      <Group direction="vertical" gap={4}>
        {hasValidDepartures && <div>
          {promotionalPrice && <>
            <PriceRowPriceCaption>{cabinTypeLabels[priceFrom.cabinCategory] ?? priceFrom.cabinCategory}</PriceRowPriceCaption>
            <Group direction="horizontal" gap={4} verticalAlign="end">
              <LuxPlusPriceStack size="S" price={promotionalPrice.total} saleUnit={promotionalPrice.type} />
              <PriceRowCaption><CruisePromotionPriceUnit promotion={promotionalPrice.promotion} /></PriceRowCaption>
            </Group>
            {promotionalPrice.perPersonPrice !== undefined && <Caption variant="medium" weight="bold" colour="success-dark">
              <FormattedMessage
                id="UDli10X2x9NsPnjIDvB0"
                defaultMessage="{price}/person"
                description="Per-person equivalent shown under a fixed cruise promotional total price"
                values={{ price: <FormatCurrency value={promotionalPrice.perPersonPrice} /> }}
              />
            </Caption>}
          </>}
          {!promotionalPrice && <>
            <PriceRowPriceCaption>{fromCaption}</PriceRowPriceCaption>
            <Group direction="horizontal" gap={4} verticalAlign="end">
              <LuxPlusPriceStack
                size="S"
                price={price.primaryPrice.total}
                memberPrice={memberPrice?.primaryPrice.total}
                saleUnit={price.primaryPrice.type}
                mode={memberPriceType === 'STANDARD_PRICE' ? 'member-only' : undefined}
              />
            </Group>
            <PriceRowCaption>
              {intl.formatMessage({
                id: 'F9_v4gAnxcZCq6ZVxwDc',
                defaultMessage: 'Twin share',
                description: 'Cruise price caption stating the twin-share/double-occupancy basis: two passengers sharing one cabin. This is an occupancy/pricing basis and does not specify or promise two separate beds.',
              })}
            </PriceRowCaption>
          </>}

          {!header && priceFrom.discountPills?.discountPercentage && <PriceRowValueDiscount
            type="estimate"
            discountPercentage={priceFrom.discountPills.discountPercentage}
          />}
        </div>}
      </Group>

      {!hasValidDepartures && <Group direction="vertical" verticalAlign="center" fullWidth>
        <BodyText variant="large" weight="bold" align="center">
          {intl.formatMessage({
            id: 'hZqiWh9QZkxCrRGrIaYZ',
            defaultMessage: 'This offer has sold out',
            description: 'Message displayed when cruise offer has no available departures',
          })}
        </BodyText>
      </Group>}
    </>
  )
}

export default CruisePriceRoomTypeInfo
